import React, { useEffect, useState } from "react";
import { HighlightSelectBox, MyPaper, SectionPaper, TextField } from "../../../components";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid2 as Grid, IconButton, InputBase, Stack, Tooltip, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { SelectBoxOption } from "../../../interfaces";
import { ExchangeData, useCalculateCostingMutation, useGetCurrencyQuery, useLazyGetBOMTreeByIdQuery, useUpdatePricingMutation } from "../../../redux/services";
import { StyledDataGrid } from "../../workbook/actions/actionTable";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { DeleteOutline, ExitToApp, CurrencyExchange, Save } from "@mui/icons-material";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "../../../router/routes";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { resetPricingSlice } from "../../../redux/slices/pricing";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { useSnackbar } from "notistack";
import { addIdForAssembly, CostingTypes, OperationTypes, pricingOutdatedUpdateFn } from "../utils";
import { ActionType, CostGroupError, CustomCostError } from "../../../redux/slices/pricing/types";
import { StyledInputBase } from "../../../components/documents/addEditDocument";
import { CostGroup, CostGroupItem, CustomCost, formatCurrency } from "pdf-generator";
import { ChildProps } from "..";
import { getUserSelectedCurrency } from "../../../utils";

export interface CostGroupItemError {
    _id: number,
    description: boolean,
    value: boolean,
    quantity: boolean,
    units: boolean,
}

export const TypeOptions = [
    { label: "Fixed", value: "fixed" },
    { label: "Percentage", value: "percentage" },
];

const AddCostGroup = ({ count = 0, onAddCostGroup }: { count: number; onAddCostGroup: (value: any) => void }) => {
    const dispatch = useAppDispatch();

    const [costGroup, setCostGroup] = useState({
        _id: count,
        title: "",
        value: 0,
        discount_percentage: 0,
        quantity: 1,
        total_amount: 0,
        costs: []
    });

    const [errors, setErrors] = useState({
        title: false,
        quantity: false,
    });

    const updateState = (key: string, value: string | number) => {
        setCostGroup((p) => ({ ...p, [key]: value }))
        setErrors((p) => ({ ...p, [key]: !value }))
    }

    const valid = () => {
        const obj = {
            title: !costGroup.title.trim(),
            quantity: !costGroup.quantity,
        };
        setErrors(obj);
        return Object.values(obj).every(o => o === false);
    }

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                Add Cost Group
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <TextField
                            label={"Description"}
                            value={costGroup.title}
                            required={true}
                            fullWidth
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Description"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateState("title", e.target.value)
                            }
                            error={errors.title}
                            helperText={
                                errors.title ? "Please enter the description" : ""
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            label={"Discount Percentage"}
                            value={costGroup.discount_percentage}
                            required={false}
                            fullWidth
                            type={"number"}
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Discount Percentage"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setCostGroup((p) => ({ ...p, discount_percentage: Number(e.target.value) ?? 0 }))
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            label={"Quantity"}
                            value={costGroup.quantity}
                            required={true}
                            fullWidth
                            type={"number"}
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Quantity"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateState("quantity", Number(e.target.value) ?? 1)
                            }
                            error={errors.quantity}
                            helperText={
                                errors.quantity ? "Please enter a valid quantity" : ""
                            }
                        />
                    </Grid>

                    <Grid size={12}>
                        <TextField
                            label={"Total Amount"}
                            value={costGroup.total_amount}
                            fullWidth
                            size={"small"}
                            variant="outlined"
                            placeholder={"Enter Total Amount"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateState("total_amount", Number(e.target.value) ?? 0)
                            }
                            viewOnly
                        />
                    </Grid>
                </Grid>
            </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
            <Stack
                sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" }
                }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(closeDialog())}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        if (valid()) {
                            const cg = {
                                _id: count,
                                title: costGroup?.title ?? "",
                                value: costGroup?.value ?? 0,
                                discount_percentage: costGroup?.discount_percentage ?? 0,
                                quantity: costGroup?.quantity ?? 1,
                                total_amount: costGroup?.total_amount ?? 0,
                                costs: []
                            };
                            onAddCostGroup(cg);
                            dispatch(closeDialog())
                        }
                    }}
                >
                    Add
                </Button>
            </Stack>
        </DialogActions>
    </>);
}

const ViewDetail = ({ readOnly, base_currency, costs = [], title = '', currencyOptions = [], onSaveItems }: { readOnly: boolean; base_currency: string; costs: any[]; title: string; currencyOptions: SelectBoxOption[]; onSaveItems: (costs: any) => void; }) => {
    const dispatch = useAppDispatch();

    const [currentItems, setItems] = React.useState<CostGroupItem[]>(costs);

    const [errors, setErrors] = React.useState<CostGroupItemError[]>([]);

    const updateItems = (id: number | string, key: string, value: string | number) => {
        setItems((prevRows) => prevRows.map(p => {
            if (p._id === id) {
                return { ...p, [key]: value }
            } else {
                return p;
            }
        }))
    }

    const addRow = () => {
        setItems((prev) => ([...prev, {
            _id: (prev.at(-1)?._id || 0) + 1,
            description: "",
            units: "fixed",
            value: 0,
            quantity: 1,
            total_cost: 0
        }]))
    }

    const triggerDelete = (id: number) => {
        dispatch(openConfirmationDialog({
            title: "Delete Operation",
            body: "Are you sure want to delete?",
            positiveBtn: "Delete",
            negativeBtn: "Cancel",
            onOk: () => onDelete(id),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    }

    const onDelete = (id: number) => {
        setItems((prevRows) => prevRows.filter(p => p._id !== id));
        dispatch(closeConfirmationDialog())
    }

    const validate = () => {
        let isValid = true;

        const errs = currentItems.map((c) => {
            let obj = {
                _id: c?._id,
                description: !c?.description?.trim(),
                value: !c.value,
                quantity: !c.quantity,
                units: !CostingTypes.find(ct => ct.value === c?.units),
            };

            return obj;
        });

        isValid = !errs.map((e) => Object.values(e).some(e => e === true)).some(e => e === true);

        setErrors(errs);

        return isValid;
    };

    const Columns = React.useMemo(() => {
        let defs: GridColDef[] = [
            {
                field: "description",
                width: 250,
                headerName: "Description",
                valueGetter: (value, row, column, apiRef) => row?.description,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        required
                        placeholder="Enter Description"
                        type="text"
                        value={params.row.description}
                        onChange={(e) => updateItems(params.row._id, 'description', e.target.value)}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={errors.find(e => params.row._id === e?._id)?.description}
                    />
                }
            },
            {
                field: "units",
                width: 200,
                headerName: "UoM",
                valueGetter: (value, row, column, apiRef) => row?.units,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        required
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={CostingTypes.find((ty: SelectBoxOption) => ty.value === params.row.units) ?? null}
                        options={CostingTypes}
                        onChange={(data: SelectBoxOption) => updateItems(params.row._id, 'units', data?.value as string)}
                        error={errors.find(e => params.row._id === e?._id)?.units}
                    />
                }
            },
            {
                field: "value",
                width: 200,
                headerName: "Value",
                valueGetter: (value, row, column, apiRef) => row?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        required
                        placeholder="Enter Value"
                        type="number"
                        value={params.row.value}
                        onChange={(e) => {
                            let currentValue = Number(e.target.value) ?? 0;
                            setItems((prevRows) => prevRows.map(p => {
                                if (p._id === params.row._id) {
                                    return { ...p, value: currentValue, total_cost: currentValue * p.quantity }
                                } else {
                                    return p;
                                }
                            }))
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={errors.find(e => params.row._id === e?._id)?.value}
                    />
                }
            },
            {
                field: "quantity",
                width: 180,
                headerName: "Quantity",
                valueGetter: (value, row, column, apiRef) => row?.quantity,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        required
                        placeholder="Enter Quantity"
                        type="number"
                        value={params.row.quantity}
                        onChange={(e) => {
                            let currentValue = Number(e.target.value) ?? 1;
                            setItems((prevRows) => prevRows.map(p => {
                                if (p._id === params.row._id) {
                                    return { ...p, quantity: currentValue, total_cost: currentValue * p.value }
                                } else {
                                    return p;
                                }
                            }))
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={errors.find(e => params.row._id === e?._id)?.quantity}
                    />
                }
            },
            {
                field: "total_cost",
                width: 200,
                headerName: "Total Cost",
                type: "number",
                align: 'right',
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.total_cost ?? 0, base_currency, false)
            },
            {
                field: "delete",
                headerName: "Delete",
                width: 100,
                renderCell: (params: GridRenderCellParams) => {
                    return (
                        <IconButton onClick={() => triggerDelete(params.row?._id)}>
                            <DeleteOutline color={"error"} />
                        </IconButton>
                    );
                },
                disableColumnMenu: true,
                sortable: false,
            }
        ];

        if (readOnly) {
            defs.pop();
        }

        return defs;
        // eslint-disable-next-line
    }, [costs, currencyOptions, errors, base_currency, readOnly])

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                {title}
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack sx={{ mt: 1 }}>
                <SectionPaper title={"Cost Items"} primaryBtn={readOnly ? undefined : {
                    title: "Add Cost Item",
                    variant: 'contained',
                    onClick: addRow
                }} hideExpandCollapse>
                    <StyledDataGrid
                        saveLocal={false}
                        columns={Columns}
                        rows={currentItems}
                        // loading={isLoading}
                        getRowId={(row: any) => row?._id}
                        onCellDoubleClick={(e) => (e.isEditable = false)}
                        sx={{
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "white",
                            },
                            '.MuiDataGrid-virtualScroller': {
                                overflow: 'auto !important',
                                height: "auto !important"
                            },
                            "& .MuiInputBase-root": {
                                border: "1px solid rgba(224, 224, 224, 1)",
                                borderRadius: 1,
                                px: 1,
                                py: 0,
                            },
                        }}
                        showToolbar
                        hideFooter
                    />
                </SectionPaper>
            </Stack>
        </DialogContent>
        {!readOnly &&
            <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
                <Stack
                    sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        flexDirection: { xs: "column", md: "row" }
                    }}>
                    <Button
                        variant="outlined"
                        onClick={() => dispatch(closeDialog())}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => {
                            if (validate()) {
                                onSaveItems(currentItems);
                                dispatch(closeDialog());
                            }
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            </DialogActions>
        }
    </>);
}

export const EditSummary: React.FC<ChildProps> = ({ refetchCosting, refetch, costingData, readOnly }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const { costing, hasCosting } = costingData;

    const currencyData = useGetCurrencyQuery(null);
    const [getBOMTree, treeResponse] = useLazyGetBOMTreeByIdQuery();
    const [calculateCost, calculationRes] = useCalculateCostingMutation();
    const [updatePricing, updateRes] = useUpdatePricingMutation();

    const isLoading = calculationRes.isLoading || updateRes.isLoading || treeResponse.isFetching || treeResponse.isLoading;

    const { pricing, auth } = useAppSelector(store => store);

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const base_currency = costing?.pricing_config?.exchange_rates?.base_currency ?? pricing?.exchange_rates?.base_currency ?? DEFAULT_CURRENCY;

    const [hasCalculated, setHasCalculated] = React.useState(hasCosting);

    const [marginRatio, setMarginRatio] = React.useState<string | number>(0);

    const [costGroupRows, setCostRows] = React.useState<CostGroup[]>([]);
    const [cGError, setCGError] = React.useState<CostGroupError[]>([]);

    const [otherCostsRows, setOtherRows] = React.useState<CustomCost[]>([]);
    const [otherCostsError, setOtherCostsError] = React.useState<CustomCostError[]>([]);

    const [discountRows, setDiscountRows] = React.useState<CustomCost[]>([]);
    const [discountsError, setDiscountsError] = React.useState<CustomCostError[]>([]);

    const [taxRows, setTaxRows] = React.useState<CustomCost[]>([]);
    const [taxesError, setTaxesError] = React.useState<CustomCostError[]>([]);

    const addCostGroupRow = () => {
        dispatch(openDialog({
            title: "",
            body: <AddCostGroup
                count={(costGroupRows?.at(-1)?._id || 0) + 1}
                onAddCostGroup={(costGroup) => {
                    setCostRows((prevRows) => [...prevRows, costGroup])
                    setHasCalculated(false);
                }}
            />,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            enablePadding: false
        }));
    };

    const updateOtherCostsState = (id: number | string, key: string, value: string | number | boolean) => {
        setOtherRows((prevRows) => prevRows.map(p => {
            if (p._id === id) {
                return { ...p, [key]: value }
            } else {
                return p;
            }
        }));
    }

    const updateDiscountState = (id: number | string, key: string, value: string | number | boolean) => {
        setDiscountRows((prevRows) => prevRows.map(p => {
            if (p._id === id) {
                return { ...p, [key]: value }
            } else {
                return p;
            }
        }));
    }

    const updateTaxState = (id: number | string, key: string, value: string | number | boolean) => {
        setTaxRows((prevRows) => prevRows.map(p => {
            if (p._id === id) {
                return { ...p, [key]: value }
            } else {
                return p;
            }
        }));
    }

    const openDetail = (row: any) => {
        if (row?._id === costGroupRows[0]?._id) {
            navigate(AppRoutes.viewPricingCostingDetail(id))
        } else {
            dispatch(openDialog({
                title: "",
                maxWidth: 'lg',
                body: <ViewDetail readOnly={readOnly} base_currency={base_currency} {...row} currencyOptions={currencyData?.data ?? []} onSaveItems={(costs: any) => {
                    setCostRows((prevRows) => prevRows.map(p => {
                        if (p._id === row?._id) {
                            const currentValue = costs?.reduce((acc: number, item: { total_cost: number }) => acc + item.total_cost, 0) ?? 0;
                            return { ...p, costs: costs, value: currentValue, total_amount: Number((currentValue * p.quantity * (1 - (p.discount_percentage || 0) / 100)).toFixed(2)) }
                        } else {
                            return p;
                        }
                    }));
                    setHasCalculated(false);
                }} />,
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false
            }));
        }
    }

    const addOtherCost = () => {
        setOtherRows((prevRows) => [...prevRows, {
            _id: (prevRows?.at(-1)?._id || 0) + 1,
            description: "",
            type: "fixed",
            subtract: false,
            value: 0,
            amount: 0,
        }])
    }

    const addDiscount = () => {
        setDiscountRows((prevRows) => [...prevRows, {
            _id: (prevRows?.at(-1)?._id || 0) + 1,
            description: "",
            type: "fixed",
            subtract: false,
            value: 0,
            amount: 0,
        }])
    }

    const addTax = () => {
        setTaxRows((prevRows) => [...prevRows, {
            _id: (prevRows?.at(-1)?._id || 0) + 1,
            description: "",
            type: "fixed",
            subtract: false,
            value: 0,
            amount: 0,
        }])
    }

    const triggerDelete = (id: number, type: ActionType) => {
        dispatch(openConfirmationDialog({
            title: "Delete Operation",
            body: "Are you sure want to delete?",
            positiveBtn: "Delete",
            negativeBtn: "Cancel",
            onOk: () => onDelete(id, type),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }));
    }

    const onDelete = (id: number, type: ActionType) => {
        let action: any = setCostRows;
        if (type === "discount") {
            action = setDiscountRows;
        } else if (type === "tax") {
            action = setTaxRows;
        } else if (type === "other_cost") {
            action = setOtherRows;
        }
        action((prevRows: any[]) => prevRows.filter(p => p._id !== id));
        setHasCalculated(false);
        dispatch(closeConfirmationDialog())
    }

    const CostGroupColumns = React.useMemo(() => {
        let columns: GridColDef[] = [
            {
                field: "item_number",
                width: 200,
                headerName: "Item Number",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.item_number ?? "N/A"
            },
            {
                field: "title",
                width: 250,
                headerName: "Description",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.title,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="master_item_name"
                        placeholder="Enter Description"
                        type="text"
                        sx={{
                            borderWidth: params.row._id === 0 ? "0 !important" : 1
                        }}
                        value={params.row.title}
                        onChange={(e) => {
                            setCostRows((prevRows) => prevRows.map(p => {
                                if (p._id === params.row._id) {
                                    return { ...p, title: e.target.value }
                                } else {
                                    return p;
                                }
                            }))
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={cGError.find(e => params.row._id === e?._id)?.title}
                        readOnly={params.row._id === 0}
                    />
                }
            },
            {
                field: "value",
                width: 200,
                headerName: "Value",
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.value || 0, base_currency, false),
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {!hasCosting && params?.row?._id === 0 ? <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip> : formatCurrency(params?.row?.value || 0, base_currency, false)}
                    </Typography>
                }
            },
            {
                field: "discount_percentage",
                width: 250,
                headerName: "Discount Percentage",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.discount_percentage,
                renderCell: (params: GridRenderCellParams) => {
                    return <InputBase
                        id="discount_percentage"
                        placeholder="Enter Percentage"
                        type="number"
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        value={params.row.discount_percentage}
                        onChange={(e) => {
                            let currentValue = Number(e.target.value) ?? 0;
                            setCostRows((prevRows) => prevRows.map(p => {
                                if (p._id === params.row._id) {
                                    return { ...p, discount_percentage: currentValue, total_amount: Number((p.value * p.quantity * (1 - currentValue / 100)).toFixed(2)) }
                                } else {
                                    return p;
                                }
                            }))
                            setHasCalculated(false);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                    />
                }
            },
            {
                field: "quantity",
                width: 180,
                headerName: "Quantity",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.quantity,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="quantity"
                        placeholder="Enter Quantity"
                        type="number"
                        value={params.row.quantity}
                        onChange={(e) => {
                            let currentValue = Number(e.target.value) ?? 0;
                            setCostRows((prevRows) => prevRows.map(p => {
                                if (p._id === params.row._id) {
                                    return { ...p, quantity: currentValue, total_amount: Number((p.value * currentValue * (1 - (p.discount_percentage ?? 0) / 100)).toFixed(2)) }
                                } else {
                                    return p;
                                }
                            }))
                            setHasCalculated(false);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={cGError.find(e => params.row._id === e?._id)?.quantity}
                    />
                }
            },
            {
                field: "total_amount",
                width: 200,
                headerName: "Total Amount",
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.total_amount || 0, base_currency, false),
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {!hasCosting && params?.row?._id === 0 ? <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip> : formatCurrency(params?.row?.total_amount || 0, base_currency, false)}
                    </Typography>
                }
            },
            {
                field: "costs",
                // width: 180,
                headerName: "Details",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return (
                        <Tooltip title={"View Details"}>
                            <IconButton onClick={() => openDetail(params?.row)}>
                                <ExitToApp color={"warning"} />
                            </IconButton>
                        </Tooltip>
                    );
                },
                valueGetter: (value, row, column, apiRef) => row?.costs,
            },
            {
                field: "delete",
                headerName: "Delete",
                width: 100,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    const isDisable = params.row?._id === costGroupRows[0]?._id;
                    return (
                        <IconButton disabled={isDisable} onClick={() => triggerDelete(params.row?._id, "cost")}>
                            <DeleteOutline color={isDisable ? "disabled" : "error"} />
                        </IconButton>
                    );
                },
            }
        ];

        if (readOnly) {
            columns.pop();
        }

        return columns;
        // eslint-disable-next-line
    }, [costGroupRows, cGError, hasCosting, base_currency, readOnly])

    const OtherColumns = React.useMemo(() => {
        let columns: GridColDef[] = [
            {
                field: "description",
                width: 250,
                headerName: "Description",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.description,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="discount_description"
                        placeholder="Enter Description"
                        type="text"
                        value={params.row.description}
                        onChange={(e) => {
                            updateOtherCostsState(params.row._id, 'description', e.target.value);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={otherCostsError.find(e => params.row._id === e?._id)?.description}
                    />
                }
            },
            {
                field: "type",
                width: 200,
                headerName: "Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.type?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="other-type"
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={TypeOptions.find(ty => ty.value === params.row.type) ?? null}
                        options={TypeOptions}
                        onChange={(data: SelectBoxOption) => {
                            updateOtherCostsState(params.row._id, 'type', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "subtract",
                width: 200,
                headerName: "Operation Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.subtract?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="other-operation-type"
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={OperationTypes.find(ty => ty.value === params.row.subtract) ?? null}
                        options={OperationTypes}
                        onChange={(data: SelectBoxOption) => {
                            updateOtherCostsState(params.row._id, 'subtract', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "value",
                width: 250,
                headerName: "Value",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="value"
                        placeholder="Enter Value"
                        type="number"
                        value={params.row.value}
                        onChange={(e) => {
                            updateOtherCostsState(params.row._id, 'value', Number(e.target.value) ?? 0);
                            setHasCalculated(false);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={otherCostsError.find(e => params.row._id === e?._id)?.value}
                    />
                }
            },
            {
                field: "amount",
                width: 180,
                headerName: "Total Cost",
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.amount ?? 0, base_currency, false),
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(params?.row?.amount ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}
                    </Typography>
                }
            },
            {
                field: "delete",
                headerName: "Delete",
                width: 100,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return (
                        <IconButton onClick={() => triggerDelete(params.row?._id, "other_cost")}>
                            <DeleteOutline color={"error"} />
                        </IconButton>
                    );
                },
            }
        ];

        if (readOnly) {
            columns.pop();
        }

        return columns;
        // eslint-disable-next-line
    }, [otherCostsRows, otherCostsError, hasCalculated, base_currency, readOnly])

    const DiscountColumns = React.useMemo(() => {
        let columns: GridColDef[] = [
            {
                field: "description",
                width: 250,
                headerName: "Description",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.description,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="discount_description"
                        placeholder="Enter Description"
                        type="text"
                        value={params.row.description}
                        onChange={(e) => {
                            updateDiscountState(params.row._id, 'description', e.target.value);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={discountsError.find(e => params.row._id === e?._id)?.description}
                    />
                }
            },
            {
                field: "type",
                width: 200,
                headerName: "Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.type?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="discount-type"
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={TypeOptions.find(ty => ty.value === params.row.type) ?? null}
                        options={TypeOptions}
                        onChange={(data: SelectBoxOption) => {
                            updateDiscountState(params.row._id, 'type', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "subtract",
                width: 200,
                headerName: "Operation Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.subtract?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="adjustment-operation-type"
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={OperationTypes.find(ty => ty.value === params.row.subtract) ?? null}
                        options={OperationTypes}
                        onChange={(data: SelectBoxOption) => {
                            updateDiscountState(params.row._id, 'subtract', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "value",
                width: 250,
                headerName: "Value",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="value"
                        placeholder="Enter Value"
                        type="number"
                        value={params.row.value}
                        onChange={(e) => {
                            updateDiscountState(params.row._id, 'value', Number(e.target.value) ?? 0);
                            setHasCalculated(false);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={discountsError.find(e => params.row._id === e?._id)?.value}
                    />
                }
            },
            {
                field: "amount",
                width: 180,
                headerName: "Total Cost",
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.amount ?? 0, base_currency, false),
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(params?.row?.amount ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}
                    </Typography>
                }
            },
            {
                field: "delete",
                headerName: "Delete",
                width: 100,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return (
                        <IconButton onClick={() => triggerDelete(params.row?._id, "discount")}>
                            <DeleteOutline color={"error"} />
                        </IconButton>
                    );
                },
            }
        ];

        if (readOnly) {
            columns.pop();
        }

        return columns;
        // eslint-disable-next-line
    }, [discountRows, discountsError, hasCalculated, base_currency, readOnly])

    const TaxColumns = React.useMemo(() => {
        let columns: GridColDef[] = [
            {
                field: "description",
                width: 250,
                headerName: "Description",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.description,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="tax_name"
                        placeholder="Enter Description"
                        type="text"
                        value={params.row.description}
                        onChange={(e) => {
                            updateTaxState(params.row._id, 'description', e.target.value);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={taxesError.find(e => params.row._id === e?._id)?.description}
                    />
                }
            },
            {
                field: "type",
                width: 200,
                headerName: "Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.type?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="tax-type"
                        disableClearable
                        label={""}
                        margin={"dense"}
                        fullWdith
                        value={TypeOptions.find(ty => ty.value === params.row.type) ?? null}
                        options={TypeOptions}
                        onChange={(data: SelectBoxOption) => {
                            updateTaxState(params.row._id, 'type', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "subtract",
                width: 200,
                headerName: "Operation Type",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.subtract?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <HighlightSelectBox
                        id="tax-operation-type"
                        disableClearable
                        label={""}
                        fullWdith
                        margin={"dense"}
                        value={OperationTypes.find(ty => ty.value === params.row.subtract) ?? null}
                        options={OperationTypes}
                        onChange={(data: SelectBoxOption) => {
                            updateTaxState(params.row._id, 'subtract', data?.value);
                            setHasCalculated(false);
                        }}
                    />
                }
            },
            {
                field: "value",
                width: 250,
                headerName: "Value",
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => row?.value,
                renderCell: (params: GridRenderCellParams) => {
                    return <StyledInputBase
                        id="tax_value"
                        placeholder="Enter Value"
                        type="number"
                        value={params.row.value}
                        onChange={(e) => {
                            updateTaxState(params.row._id, 'value', Number(e.target.value) ?? 0);
                            setHasCalculated(false);
                        }}
                        onKeyDown={(event) => {
                            event.stopPropagation();
                        }}
                        error={taxesError.find(e => params.row._id === e?._id)?.value}
                    />
                }
            },
            {
                field: "amount",
                width: 180,
                headerName: "Total Cost",
                type: "number",
                align: 'right',
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                valueGetter: (value, row, column, apiRef) => formatCurrency(row?.amount ?? 0, base_currency, false),
                renderCell: (params: GridRenderCellParams) => {
                    return <Typography
                        variant="body2"
                    >
                        {hasCalculated ? formatCurrency(params?.row?.amount ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}
                    </Typography>
                }
            },
            {
                field: "delete",
                headerName: "Delete",
                width: 100,
                sortable: false,
                hideSortIcons: true,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => {
                    return (
                        <IconButton onClick={() => triggerDelete(params.row?._id, "tax")}>
                            <DeleteOutline color={"error"} />
                        </IconButton>
                    );
                },
            }
        ];

        if (readOnly) {
            columns.pop();
        }

        return columns;
        // eslint-disable-next-line
    }, [taxRows, taxesError, hasCalculated, base_currency, readOnly]);

    const validate = () => {
        let isValid = true;

        let cGErrors = costGroupRows.slice(1).map((c, idx) => ({
            _id: idx + 1,
            title: !c.title?.trim(),
            quantity: !(Number(c.quantity) || 0),
        }))

        let otherCostsErr = otherCostsRows.map((d, idx) => ({
            _id: idx + 1,
            description: !d.description?.trim(),
            value: (Number(d.value) || 0) <= 0,
        }));

        let discountsErr = discountRows.map((d, idx) => ({
            _id: idx + 1,
            description: !d.description?.trim(),
            value: (Number(d.value) || 0) <= 0,
        }));

        const taxesErr = taxRows.map((d, idx) => ({
            _id: idx + 1,
            description: !d.description?.trim(),
            value: (Number(d.value) || 0) <= 0,
        }));

        cGErrors.forEach((e) => Object.values(e).forEach(e => {
            if (e === true) {
                isValid = false;
            }
        }))

        otherCostsErr.forEach((e) => Object.values(e).forEach(e => {
            if (e === true) {
                isValid = false;
            }
        }))

        discountsErr.forEach((e) => Object.values(e).forEach(e => {
            if (e === true) {
                isValid = false;
            }
        }))

        taxesErr.forEach((e) => Object.values(e).forEach(e => {
            if (e === true) {
                isValid = false;
            }
        }));

        setCGError(cGErrors);

        setOtherCostsError(otherCostsErr);

        setDiscountsError(discountsErr);

        setTaxesError(taxesErr);

        return isValid;
    };

    const onSave = async (saveAndCalculate: boolean) => {
        if (validate()) {
            dispatch(openBackdrop("Saving the pricing..."));
            const { basic_details, bom_cost, exchange_rates } = pricing;

            await updatePricing({
                pricing_id: id,
                bom_id: basic_details?.bom_id?._id as string,
                payload: {
                    bom_cost: addIdForAssembly(bom_cost), // TODO: To be taken up by backend
                    pricing_name: basic_details.pricing_name,
                    pricing_model: basic_details?.pricing_model?.value,
                    remarks: basic_details?.remarks,
                    bom_cost_quantity: costGroupRows[0]?.quantity || 1,
                    margin_ratio: typeof marginRatio === "string" ? 0 : marginRatio,
                    bom_cost_discount_percentage: costGroupRows[0]?.discount_percentage || 0,
                    exchange_rates,
                    cost_groups: costGroupRows.slice(1).map((c) => ({
                        title: c.title,
                        quantity: Number(c.quantity) || 1,
                        discount_percentage: Number(c.discount_percentage) || 0,
                        total_amount: Number(c.total_amount) || 0,
                        // value: c.value,
                        costs: c.costs?.map((cost: CostGroupItem) => ({
                            position: cost._id,
                            description: cost.description,
                            units: cost.units,
                            value: Number(cost.value) || 0,
                            quantity: Number(cost.quantity) || 0,
                            total_cost: Number(cost.total_cost) || 0
                        }))
                    })),
                    other_costs: otherCostsRows.map((d, idx) => ({
                        position: idx + 1,
                        description: d.description,
                        type: d.type,
                        value: Number(d.value) || 0,
                        subtract: Boolean(d.subtract)
                    })),
                    discounts: discountRows.map((d, idx) => ({
                        position: idx + 1,
                        description: d.description,
                        type: d.type,
                        value: Number(d.value) || 0,
                        subtract: Boolean(d.subtract)
                    })),
                    taxes: taxRows.map((d, idx) => ({
                        position: idx + 1,
                        description: d.description,
                        type: d.type,
                        value: Number(d.value) || 0,
                        subtract: Boolean(d.subtract)
                    })),
                    // links: [{ object_type: getObjectType(location) as string, object_ids: [p_id] }]
                }
            })
                .unwrap()
                .then((res) => {
                    if (saveAndCalculate && exchange_rates) {
                        onCalculate(exchange_rates as ExchangeData);
                        refetch && refetch();
                    } else {
                        enqueueSnackbar(res.message ?? "Pricing updated successfully!", { variant: "success" });
                        refetch && refetch();
                    }
                })
                .catch((error) => {
                    enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                    console.error(`Error updating pricing: ${JSON.stringify(error)}`)
                })
                .finally(() => {
                    dispatch(closeBackdrop());
                })
        } else {
            enqueueSnackbar("Please fill all the required fields", { variant: "error" });
        }
    }

    const onCalculate = async (exchange_rates: ExchangeData) => {
        dispatch(openBackdrop("Calculating Cost..."));
        await calculateCost({ pricing_id: id, exchange_rates: exchange_rates })
            .unwrap()
            .then((res) => {
                setHasCalculated(true);
                refetchCosting();
            }).catch((error) => {
                if (error?.data?.title === "BOM Outdated" && error?.data?.status === 409) {
                    dispatch(openConfirmationDialog({
                        title: "Pricing Update",
                        body: "The BOM of this pricing is updated, do you want to update the pricing with latest BOM changes ?",
                        negativeBtn: "Cancel",
                        positiveBtn: "Yes",
                        onOk: () => {
                            pricingOutdatedUpdateFn(getBOMTree, updatePricing, dispatch, pricing, id, () => {
                                if (refetch) {
                                    refetch();
                                }
                                refetchCosting();
                            });
                        },
                        onNegativeBtn: () => dispatch(closeConfirmationDialog())
                    }))
                }
                enqueueSnackbar(error?.data?.title ?? "Unable to calculate the pricing", { variant: "error" });
                console.error(`Error calculating pricing: ${JSON.stringify(error)}`)
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    useEffect(() => {
        const commonBOM = {
            _id: 0,
            item_number: `${pricing.bom_cost?.sequence_id ?? ""}-r${pricing.bom_cost?.version ?? 0}`,
            title: pricing.bom_cost?.short_description ?? "",
            value: costing?.calculated_cost?.final_total_cost_in_pricing_currency?.cost ?? 0,
            discount_percentage: pricing.bom_cost_discount_percentage ?? 0,
            quantity: pricing.bom_cost_quantity ?? 1,
            total_amount: Number(((costing?.calculated_cost?.final_total_cost_in_pricing_currency?.cost ?? 0) * (pricing?.bom_cost_quantity ?? 1) * (1 - (pricing?.bom_cost_discount_percentage ?? 0) / 100)).toFixed(0)),
            costs: []
        };
        if (pricing.cost_groups?.length > 0) {
            setCostRows([commonBOM, ...pricing.cost_groups.map((c, idx) => {
                const currentValue = c?.costs?.reduce((acc: number, item: { total_cost: number }) => acc + item.total_cost, 0) ?? 0;
                return {
                    ...c,
                    total_amount: Number((currentValue * c.quantity * (1 - (c.discount_percentage || 0) / 100)).toFixed(2)),
                    value: currentValue,
                    _id: idx + 1,
                    costs: c.costs?.map((c: CostGroupItem, index: number) => ({ ...c, _id: index + 1 })) ?? [],
                    item_number: "N/A",
                }
            })])
        } else {
            setCostRows([commonBOM]);
        }

        if (pricing.other_costs?.length > 0) {
            setOtherRows([...pricing.other_costs.map((c, idx) => {
                let other_cost = costing?.pricing_config?.other_costs?.[idx];
                return { ...c, _id: idx + 1, amount: other_cost?.amount ?? 0 };
            })])
        }

        if (pricing.discounts?.length > 0) {
            setDiscountRows([...pricing.discounts.map((c, idx) => {
                let discount = costing?.pricing_config?.discounts?.[idx];
                return { ...c, _id: idx + 1, amount: discount?.amount ?? 0 };
            })])
        }

        if (pricing.taxes?.length > 0) {
            setTaxRows([...pricing.taxes.map((c, idx) => {
                let tax = costing?.pricing_config?.taxes?.[idx];
                return { ...c, _id: idx + 1, amount: tax?.amount ?? 0 };
            })])
        }
        if (pricing?.margin_ratio) {
            setMarginRatio(pricing?.margin_ratio);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(pricing), JSON.stringify(hasCosting)])

    useEffect(() => {
        setHasCalculated(hasCosting);
        // eslint-disable-next-line 
    }, [hasCosting])

    useEffect(() => {
        if (refetch) {
            refetch();
        }
        // eslint-disable-next-line 
    }, [])

    return (
        (<Stack spacing={2}>

            <SectionPaper title="Major Items" primaryBtn={readOnly ? undefined : {
                title: "Add Major Item",
                variant: 'contained',
                onClick: addCostGroupRow,
            }} renderButtons={readOnly ? [] : [
                <Tooltip title={pricing?.released ? `${pricing?.sequenceId ?? "..."} is published` : `Save ${pricing?.sequenceId ?? "..."}`}>
                    <IconButton disabled={readOnly} disableRipple={pricing?.released} onClick={() => pricing?.released ? () => { } : onSave(false)}>
                        <Save color={pricing?.released ? "disabled" : "primary"} />
                    </IconButton>
                </Tooltip>
            ]}>
                <StyledDataGrid
                    saveLocal={false}
                    columns={CostGroupColumns}
                    rows={costGroupRows}
                    // loading={isLoading}
                    getRowId={(row: any) => row?._id}
                    onCellDoubleClick={(e) => (e.isEditable = false)}
                    sx={{
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "white",
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            height: "auto !important"
                        },
                        "& .MuiInputBase-root": {
                            border: "1px solid rgba(224, 224, 224, 1)",
                            borderRadius: 1,
                            px: 1,
                            py: 0,
                        },
                    }}
                    showToolbar
                    hideFooter
                />
            </SectionPaper>

            <MyPaper>
                <Grid
                    spacing={2}
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "left" }
                            }}>Total Cost</Typography></Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "right" }
                            }}>{hasCalculated ? formatCurrency((costing?.total_bom_cost ?? 0) + (costing?.total_cost_group_cost ?? 0), base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}</Typography></Grid>
                </Grid>
            </MyPaper>

            <SectionPaper title="Other Items" primaryBtn={readOnly ? undefined : {
                title: "Add Item",
                variant: 'contained',
                onClick: addOtherCost
            }} renderButtons={readOnly ? [] : [
                <Tooltip title={pricing?.released ? `${pricing?.sequenceId ?? "..."} is published` : `Save ${pricing?.sequenceId ?? "..."}`}>
                    <IconButton disableRipple={pricing?.released} onClick={() => pricing?.released ? () => { } : onSave(false)}>
                        <Save color={pricing?.released ? "disabled" : "primary"} />
                    </IconButton>
                </Tooltip>
            ]}>
                <StyledDataGrid
                    saveLocal={false}
                    columns={OtherColumns}
                    rows={otherCostsRows}
                    getRowId={(row: any) => row?._id}
                    onCellDoubleClick={(e) => (e.isEditable = false)}
                    sx={{
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "white",
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            height: "auto !important"
                        },
                        "& .MuiInputBase-root": {
                            border: "1px solid rgba(224, 224, 224, 1)",
                            borderRadius: 1,
                            px: 1,
                            py: 0,
                        },
                    }}
                    showToolbar
                    hideFooter
                />
            </SectionPaper>

            <MyPaper>
                <Grid
                    spacing={2}
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "left" }
                            }}>Total Cost (including Other Items)</Typography></Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "right" }
                            }}>{hasCalculated ? formatCurrency(costing?.gross_cost ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}</Typography></Grid>
                </Grid>
            </MyPaper>

            <SectionPaper title="Adjustments" primaryBtn={readOnly ? undefined : {
                title: "Add Adjustment",
                variant: 'contained',
                onClick: addDiscount
            }} renderButtons={readOnly ? [] : [
                <Tooltip title={pricing?.released ? `${pricing?.sequenceId ?? "..."} is published` : `Save ${pricing?.sequenceId ?? "..."}`}>
                    <IconButton disableRipple={pricing?.released} onClick={() => pricing?.released ? () => { } : onSave(false)}>
                        <Save color={pricing?.released ? "disabled" : "primary"} />
                    </IconButton>
                </Tooltip>
            ]}>
                <StyledDataGrid
                    saveLocal={false}
                    columns={DiscountColumns}
                    rows={discountRows}
                    getRowId={(row: any) => row?._id}
                    onCellDoubleClick={(e) => (e.isEditable = false)}
                    sx={{
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "white",
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            height: "auto !important"
                        },
                        "& .MuiInputBase-root": {
                            border: "1px solid rgba(224, 224, 224, 1)",
                            borderRadius: 1,
                            px: 1,
                            py: 0,
                        },
                    }}
                    showToolbar
                    hideFooter
                />
            </SectionPaper>

            <MyPaper>
                <Grid
                    spacing={2}
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "left" }
                            }}>Total Cost (including Adjustments)</Typography></Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "right" }
                            }}>{hasCalculated ? formatCurrency(costing?.gross_cost_after_discount ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}</Typography></Grid>
                </Grid>
            </MyPaper>

            <SectionPaper stack title="Margin Percentage" renderButtons={[
                <TextField
                    type="number"
                    size="small"
                    placeholder={"Enter the margin"}
                    fullWidth
                    sx={{ minWidth: { xs: 250, sm: 200, md: 250 } }}
                    onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                    value={marginRatio}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (hasCalculated) {
                            setHasCalculated(false);
                        }
                        // Allow empty input
                        if (value === "") {
                            setMarginRatio("");
                            return;
                        }

                        // Ensure value is within 0-100
                        const numericValue = parseFloat(value);
                        if (numericValue >= 0 && numericValue < 100) {
                            setMarginRatio(numericValue);
                        }
                    }}
                    InputProps={{ inputProps: { min: 0, max: 99.9, step: 0.01 } }} // step for decimals
                    viewOnly={readOnly}
                />
            ]} hideExpandCollapse isExpand={false}>
                <></>
            </SectionPaper>

            <MyPaper>
                <Grid
                    spacing={2}
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "left" }
                            }}>{`Total Cost (${marginRatio ? 'including margin' : 'before tax'})`}</Typography></Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "right" }
                            }}>{hasCalculated ? formatCurrency(costing?.gross_cost_after_margin ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}</Typography></Grid>
                </Grid>
            </MyPaper>

            <SectionPaper title="Taxes" primaryBtn={readOnly ? undefined : {
                title: "Add Tax",
                variant: 'contained',
                onClick: addTax
            }} renderButtons={readOnly ? [] : [
                <Tooltip title={pricing?.released ? `${pricing?.sequenceId ?? "..."} is published` : `Save ${pricing?.sequenceId ?? "..."}`}>
                    <IconButton disableRipple={pricing?.released} onClick={() => pricing?.released ? () => { } : onSave(false)}>
                        <Save color={pricing?.released ? "disabled" : "primary"} />
                    </IconButton>
                </Tooltip>
            ]}>
                <StyledDataGrid
                    saveLocal={false}
                    columns={TaxColumns}
                    rows={taxRows}
                    // loading={isLoading}
                    getRowId={(row: any) => row?._id}
                    onCellDoubleClick={(e) => (e.isEditable = false)}
                    sx={{
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "white",
                        },
                        '.MuiDataGrid-virtualScroller': {
                            overflow: 'auto !important',
                            height: "auto !important"
                        },
                        "& .MuiInputBase-root": {
                            border: "1px solid rgba(224, 224, 224, 1)",
                            borderRadius: 1,
                            px: 1,
                            py: 0,
                        },
                    }}
                    showToolbar
                    hideFooter
                />
            </SectionPaper>

            <MyPaper>
                <Grid
                    spacing={2}
                    container
                    sx={{
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 8
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "left" }
                            }}>Total Cost (after tax)</Typography></Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}><Typography
                            variant="body1"
                            sx={{
                                fontWeight: "bold",
                                textAlign: { xs: "left", md: "right" }
                            }}>{hasCalculated ? formatCurrency(costing?.final_price ?? 0, base_currency, false) : <Tooltip title={"Recalculate to see the updated cost"}><CurrencyExchange color='success' /></Tooltip>}</Typography></Grid>
                </Grid>
            </MyPaper>

            <Box sx={{ height: "70px" }}></Box>

            {/* Actions */}
            {!readOnly &&
                <Box sx={{
                    mt: 3,
                    position: 'fixed',
                    right: "8px",
                    bottom: "8px",
                    borderRadius: "8px",
                    background: (theme) => theme.palette.background.paper,
                    p: 2,
                    boxShadow: `rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px`
                }}>
                    <Stack
                        sx={{
                            gap: 2,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                dispatch(resetPricingSlice())
                                return navigate(AppRoutes.pricingList);
                            }}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>

                        <LoadingButton
                            variant="contained"
                            onClick={() => onSave(false)}
                            loading={isLoading}
                        >
                            Save
                        </LoadingButton>

                        <LoadingButton
                            variant="contained"
                            onClick={() => onSave(true)}
                            loading={isLoading}
                        >
                            Calculate
                        </LoadingButton>
                    </Stack>
                </Box>
            }
        </Stack>)
    );
}